/**
 * Units. The floor plan stores REAL-WORLD millimetres, never pixels
 * (docs/02 §6). Screen coordinates exist only inside the renderer.
 */
declare const brand: unique symbol
export type Mm = number & { readonly [brand]: 'Mm' }
export type Deg = number & { readonly [brand]: 'Deg' }
export type Rad = number & { readonly [brand]: 'Rad' }

export const mm = (n: number): Mm => n as Mm
export const deg = (n: number): Deg => n as Deg
export const rad = (n: number): Rad => n as Rad

export const M_TO_MM = 1000
export const metres = (n: number): Mm => (n * M_TO_MM) as Mm
export const toMetres = (n: Mm): number => n / M_TO_MM

export const degToRad = (d: Deg): Rad => ((d * Math.PI) / 180) as Rad
export const radToDeg = (r: Rad): Deg => ((r * 180) / Math.PI) as Deg

/** Normalise degrees into [0, 360). */
export const normDeg = (d: number): Deg => (((d % 360) + 360) % 360) as Deg
/** Normalise radians into (-PI, PI]. */
export function normRad(r: number): Rad {
  let x = r % (2 * Math.PI)
  if (x > Math.PI) x -= 2 * Math.PI
  if (x <= -Math.PI) x += 2 * Math.PI
  return x as Rad
}

export interface Vec2 { readonly x: Mm; readonly y: Mm }
export const vec = (x: number, y: number): Vec2 => ({ x: x as Mm, y: y as Mm })

export const add = (a: Vec2, b: Vec2): Vec2 => vec(a.x + b.x, a.y + b.y)
export const sub = (a: Vec2, b: Vec2): Vec2 => vec(a.x - b.x, a.y - b.y)
export const scale = (a: Vec2, k: number): Vec2 => vec(a.x * k, a.y * k)
export const dot = (a: Vec2, b: Vec2): number => a.x * b.x + a.y * b.y
export const cross = (a: Vec2, b: Vec2): number => a.x * b.y - a.y * b.x
export const lenSq = (a: Vec2): number => a.x * a.x + a.y * a.y
export const len = (a: Vec2): number => Math.hypot(a.x, a.y)
export const distSq = (a: Vec2, b: Vec2): number => lenSq(sub(a, b))
export const dist = (a: Vec2, b: Vec2): number => len(sub(a, b))
export function normalize(a: Vec2): Vec2 {
  const l = len(a)
  return l === 0 ? vec(0, 0) : vec(a.x / l, a.y / l)
}
/** Rotate 90 degrees counter-clockwise (y-up world). */
export const perp = (a: Vec2): Vec2 => vec(-a.y, a.x)
export const angleOf = (a: Vec2): Rad => Math.atan2(a.y, a.x) as Rad
export const lerp = (a: Vec2, b: Vec2, t: number): Vec2 =>
  vec(a.x + (b.x - a.x) * t, a.y + (b.y - a.y) * t)

/** Currency: minor units (piastres) as integers. Never floats for money. */
export type Piastres = number & { readonly [brand]: 'Piastres' }
export const egp = (pounds: number): Piastres => Math.round(pounds * 100) as Piastres
export const piastres = (n: number): Piastres => Math.round(n) as Piastres
export const toPounds = (p: Piastres): number => p / 100
export function formatEgp(p: Piastres): string {
  const neg = p < 0
  const abs = Math.abs(p)
  const s = `${Math.floor(abs / 100).toLocaleString('en-US')}.${String(abs % 100).padStart(2, '0')}`
  return `${neg ? '-' : ''}EGP ${s}`
}
